/**
 * WaveChart — minimal canvas streaming line chart.
 *
 * Drawn imperatively from the telemetry subscription (never via React state),
 * one full redraw per batch (~2/sec). Canvas keeps this cheap enough for
 * dozens of cards.
 */
export default class WaveChart {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.yMin = null;
    this.yMax = null;
    this.resize();
  }

  // Drop the smoothed y-scale so the next draw snaps to the fresh data
  // (used on a lead-on wipe).
  reset() {
    this.yMin = null;
    this.yMax = null;
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (!w || !h) return;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = w;
    this.h = h;
  }

  /**
   * @param buf      ring buffer from telemetryStore ({times, values, head, size, cap})
   * @param windowMs visible time window
   * @param now      right edge timestamp
   * @param color    line color (blue normally, grey when stale)
   */
  draw(buf, windowMs, now, color) {
    const { ctx, w, h } = this;
    if (!w || !h || !buf || buf.size === 0) return;

    const t0 = now - windowMs;

    // Autoscale y over the visible window, smoothed so the scale doesn't jump.
    let lo = Infinity, hi = -Infinity;
    const start = (buf.head - buf.size + buf.cap) % buf.cap;
    for (let i = 0; i < buf.size; i++) {
      const idx = (start + i) % buf.cap;
      if (buf.times[idx] < t0) continue;
      const v = buf.values[idx];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    if (lo === Infinity) return;
    const pad = (hi - lo) * 0.15 + 0.05;
    lo -= pad; hi += pad;
    this.yMin = this.yMin == null ? lo : this.yMin + (lo - this.yMin) * 0.2;
    this.yMax = this.yMax == null ? hi : this.yMax + (hi - this.yMax) * 0.2;

    const x = t => ((t - t0) / windowMs) * w;
    const y = v => h - ((v - this.yMin) / (this.yMax - this.yMin)) * h;

    ctx.clearRect(0, 0, w, h);

    // Faint horizontal grid
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let g = 1; g <= 3; g++) {
      const gy = (h / 4) * g + 0.5;
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke();
    }

    // Waveform path (built once for fill, once for stroke)
    let lastX = 0, lastY = 0, firstX = 0, started = false;
    ctx.beginPath();
    for (let i = 0; i < buf.size; i++) {
      const idx = (start + i) % buf.cap;
      const t = buf.times[idx];
      if (t < t0) continue;
      lastX = x(t); lastY = y(buf.values[idx]);
      if (!started) { ctx.moveTo(lastX, lastY); firstX = lastX; started = true; }
      else ctx.lineTo(lastX, lastY);
    }

    const fill = ctx.createLinearGradient(0, 0, 0, h);
    fill.addColorStop(0, color + '33');
    fill.addColorStop(1, color + '00');
    ctx.save();
    ctx.lineTo(lastX, h); ctx.lineTo(firstX, h); ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.restore();

    // Break the line across time gaps (missed data) instead of bridging them —
    // a gap wider than GAP_BREAK_MS lifts the pen so old and resumed traces
    // never connect with a false horizontal line.
    const GAP_BREAK_MS = 300;
    ctx.beginPath();
    started = false;
    let prevT = null;
    for (let i = 0; i < buf.size; i++) {
      const idx = (start + i) % buf.cap;
      const t = buf.times[idx];
      if (t < t0) continue;
      const px = x(t), py = y(buf.values[idx]);
      if (!started || (prevT != null && t - prevT > GAP_BREAK_MS)) { ctx.moveTo(px, py); started = true; }
      else ctx.lineTo(px, py);
      prevT = t;
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Emphasised endpoint
    ctx.beginPath();
    ctx.arc(lastX, lastY, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }
}
